import type { PayloadAction } from '@reduxjs/toolkit';
import type { NormalizedV2Response } from '@/types/eds/api/responses/NormalizedV2Response';
import type { GlobalSearchSearchPayload } from '@/features/globalSearch/store/types/GlobalSearchSearchPayload';
import type { GlobalSearchRecentListResponse } from '@/features/globalSearch/types/api/responses/GlobalSearchRecentListResponse';
import { GlobalSearchSearchResponse } from '@/features/globalSearch/types/api/responses/GlobalSearchSearchResponse';
import type { HttpServiceResponse } from '@/services/HttpService/types';
import { call, put, takeLatest } from 'redux-saga/effects';
import { EdsEntityCodes } from '@/features/eds/enums/EdsEntityCodes';
import EdsAdapterService from '@/features/eds/services/EdsAdapterService/EdsAdapterService';
import EdsDebugService from '@/features/eds/services/EdsDebugService/EdsDebugService';
import { edsToEntityFormat } from '@/features/eds/services/utils/edsToCodeFormat';
import { GlobalSearchClaimSetsSlots } from '@/features/globalSearch/enums/GlobalSearchClaimSetsSlots';
import {
  fetchRecentList,
  fetchRecentListFailed,
  fetchRecentListSuccess,
  incrementSearchPage,
  search,
  searchFailed,
  searchSuccess,
  setRecentListLoading,
} from '@/features/globalSearch/store/globalSearchSlice';
import { SnackType } from '@/features/snacks/enums/SnackType';
import { ClaimSetSections } from '@/domains/claimSets/enums/ClaimSetSections';
import ClaimSetHistoryVisitsService from '@/domains/claimSets/services/ClaimSetHistoryVisitsService/ClaimSetHistoryVisitsService';
import HttpService from '@/services/HttpService/HttpService';
import QueryParamsService from '@/services/QueryParamsService/QueryParamsService';
import SnacksService from '@/services/SnackService/SnackService';

const section = ClaimSetSections.GlobalSearchClaimSets;
const entities = [
  `${EdsEntityCodes.ClaimEntities}.${EdsEntityCodes.Claimants}.${EdsEntityCodes.Beneficiary}.${EdsEntityCodes.Party}.${EdsEntityCodes.Person}`,
  `${EdsEntityCodes.ClaimEntities}.${EdsEntityCodes.Claimants}.${EdsEntityCodes.Beneficiary}.${EdsEntityCodes.Party}.${EdsEntityCodes.Business}`,
  `${EdsEntityCodes.ClaimEntities}.${EdsEntityCodes.Beneficiaries}.${EdsEntityCodes.Party}.${EdsEntityCodes.Person}`,
  `${EdsEntityCodes.ClaimEntities}.${EdsEntityCodes.Beneficiaries}.${EdsEntityCodes.Party}.${EdsEntityCodes.Business}`,
  `${EdsEntityCodes.Insured}.${EdsEntityCodes.Party}.${EdsEntityCodes.Person}`,
].join(',');

export function* fetchRecentSaga() {
  try {
    yield put(setRecentListLoading(true));

    const requestUrl = `/filter/${EdsEntityCodes.ClaimSet}?${QueryParamsService.constructSearchParamsFromRecord({ page: 1, perPage: 3, includes: entities })}`;
    const history = ClaimSetHistoryVisitsService.readMany(3).map(({ claimSetId }) => Number(claimSetId));

    if (history.length === 0) return;

    const { data }: HttpServiceResponse<GlobalSearchRecentListResponse> = yield call(
      HttpService.post,
      requestUrl,
      [
        {
          propertyName: edsToEntityFormat('id'),
          values: history,
        },
      ],
      {
        baseURL: import.meta.env.VITE_API_URL_EDS,
      },
    );

    const normalizedResponse: NormalizedV2Response[] = yield call(EdsAdapterService.normalize, data.listings, {
      section,
    });

    yield put(
      fetchRecentListSuccess({
        list: normalizedResponse,
        pagination: data.pagination,
      }),
    );
  } catch (error) {
    console.error(error);
    yield put(fetchRecentListFailed());
    SnacksService.add({
      type: SnackType.Error,
      title: 'Failed to fetch recently viewed Claim Sets',
      content: 'Please try again',
      anchorOrigin: {
        horizontal: 'right',
        vertical: 'bottom',
      },
    });
  } finally {
    yield put(setRecentListLoading(false));
  }
}

export function* searchSaga({ type, payload: { value } }: PayloadAction<GlobalSearchSearchPayload>) {
  try {
    const requestUrl = `/search?${QueryParamsService.constructSearchParamsFromRecord({ searchType: 'Basic', searchValue: value, includes: entities, page: 1, perPage: 5 })}`;

    const { data }: HttpServiceResponse<GlobalSearchSearchResponse> = yield call(
      HttpService.get,
      requestUrl,
      {},
      {
        baseURL: import.meta.env.VITE_API_URL_EDS,
      },
    );

    const normalizedResponse: NormalizedV2Response[] = yield call(EdsAdapterService.normalize, data.listings, {
      section,
    });

    EdsDebugService.logResponse(EdsEntityCodes.ClaimSet, {
      rawResponse: data,
      normalizedResponse,
      dataBySlot: normalizedResponse?.[0]
        ? EdsAdapterService.generateSlotRecordIncludingEntities<GlobalSearchClaimSetsSlots>(normalizedResponse[0])
        : {},
      section,
    });

    yield put(
      searchSuccess({
        shouldRewrite: type === search.type,
        list: normalizedResponse,
        pagination: data.pagination,
      }),
    );
  } catch (error) {
    yield put(searchFailed());
    console.error(error);
    SnacksService.add({
      type: SnackType.Error,
      title: 'Failed to fetch search results',
      content: 'Please try again',
      anchorOrigin: {
        horizontal: 'right',
        vertical: 'bottom',
      },
    });
  }
}

export function* globalSearchWatcher() {
  yield takeLatest([fetchRecentList.type], fetchRecentSaga);
  yield takeLatest([search.type, incrementSearchPage.type], searchSaga);
}
